import { memo, useState, useEffect, useRef } from 'react';
import { User, Bot, Copy, Check, RefreshCw, Globe, Code, FileSearch, Image, Brain, ChevronDown, ChevronUp, FileText, Layers, Settings, Package, Download, ExternalLink, Loader2, CheckCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { MarkdownRenderer } from './MarkdownRenderer';
import type { ChatMessage as ChatMessageType, ToolCallInfo, ChatMessageArtifact, ArtifactType } from '@/types';

// Tool icon mapping
const TOOL_ICONS: Record<string, typeof Globe> = {
  web_search: Globe,
  google_search: Globe,
  code_interpreter: Code,
  code_execution: Code,
  file_search: FileSearch,
  image_generation: Image,
};

const TOOL_LABELS: Record<string, string> = {
  web_search: '웹 검색',
  google_search: 'Google 검색',
  code_interpreter: '코드 실행',
  code_execution: '코드 실행',
  file_search: '파일 검색',
  image_generation: '이미지 생성',
};

// Artifact type icon and label mapping
const ARTIFACT_ICONS: Record<ArtifactType, typeof FileText> = {
  code: Code,
  prototype: Layers,
  generated: FileText,
  config: Settings,
  asset: Package,
  export: Download,
  upload: Package,
  document: FileText,
};

const ARTIFACT_LABELS: Record<ArtifactType, string> = {
  code: '코드',
  prototype: '프로토타입',
  generated: '생성됨',
  config: '설정',
  asset: '에셋',
  export: '내보내기',
  upload: '업로드',
  document: '문서',
};

const ARTIFACT_COLORS: Record<ArtifactType, string> = {
  code: 'bg-blue-50 border-blue-200 dark:bg-blue-950/50 dark:border-blue-800',
  prototype: 'bg-purple-50 border-purple-200 dark:bg-purple-950/50 dark:border-purple-800',
  generated: 'bg-green-50 border-green-200 dark:bg-green-950/50 dark:border-green-800',
  config: 'bg-orange-50 border-orange-200 dark:bg-orange-950/50 dark:border-orange-800',
  asset: 'bg-pink-50 border-pink-200 dark:bg-pink-950/50 dark:border-pink-800',
  export: 'bg-cyan-50 border-cyan-200 dark:bg-cyan-950/50 dark:border-cyan-800',
  upload: 'bg-gray-50 border-gray-200 dark:bg-gray-950/50 dark:border-gray-800',
  document: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/50 dark:border-emerald-800',
};

const ARTIFACT_TEXT_COLORS: Record<ArtifactType, string> = {
  code: 'text-blue-700 dark:text-blue-300',
  prototype: 'text-purple-700 dark:text-purple-300',
  generated: 'text-green-700 dark:text-green-300',
  config: 'text-orange-700 dark:text-orange-300',
  asset: 'text-pink-700 dark:text-pink-300',
  export: 'text-cyan-700 dark:text-cyan-300',
  upload: 'text-gray-700 dark:text-gray-300',
  document: 'text-emerald-700 dark:text-emerald-300',
};

interface ArtifactCardProps {
  artifact: ChatMessageArtifact;
  projectId?: string;
  onConfirm?: (artifactId: string) => Promise<void>;
}

function ArtifactCard({ artifact, projectId, onConfirm }: ArtifactCardProps) {
  const [confirming, setConfirming] = useState(false);
  const Icon = ARTIFACT_ICONS[artifact.artifactType] || FileText;
  const label = ARTIFACT_LABELS[artifact.artifactType] || artifact.artifactType;
  const colorClass = ARTIFACT_COLORS[artifact.artifactType] || ARTIFACT_COLORS.generated;
  const textColorClass = ARTIFACT_TEXT_COLORS[artifact.artifactType] || ARTIFACT_TEXT_COLORS.generated;

  const handleClick = () => {
    if (projectId) {
      // Navigate to artifact in project context
      window.open(`/projects/${projectId}/artifacts/${artifact.id}`, '_blank');
    }
  };

  const handleConfirm = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onConfirm) return;

    setConfirming(true);
    try {
      await onConfirm(artifact.id);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:shadow-md border',
        colorClass
      )}
      onClick={handleClick}
    >
      <CardContent className="flex items-center gap-3 p-3">
        <div className={cn('rounded-lg p-2 bg-white/50 dark:bg-black/20', textColorClass)}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={cn('font-medium text-sm truncate', textColorClass)}>
              {artifact.name}
            </p>
            {/* Confirmation status badge */}
            {artifact.isEssential && (
              artifact.confirmed ? (
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950/50 dark:text-green-300 dark:border-green-800 shrink-0">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  컨펌됨
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/50 dark:text-yellow-300 dark:border-yellow-800 shrink-0">
                  <Clock className="h-3 w-3 mr-1" />
                  검토 대기
                </Badge>
              )
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {label} • {new Date(artifact.createdAt).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            {artifact.confirmedByName && ` • ${artifact.confirmedByName}님 컨펌`}
          </p>
        </div>

        {/* Confirm button */}
        {artifact.requiresConfirmation && onConfirm && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleConfirm}
            disabled={confirming}
            className="shrink-0 bg-white/50 dark:bg-black/20"
          >
            {confirming ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                컨펌
              </>
            )}
          </Button>
        )}

        <ExternalLink className={cn('h-4 w-4 opacity-50 shrink-0', textColorClass)} />
      </CardContent>
    </Card>
  );
}

function ToolUsageBadge({ tool }: { tool: ToolCallInfo }) {
  const Icon = TOOL_ICONS[tool.type] || Globe;
  const label = TOOL_LABELS[tool.type] || tool.type;
  const isExecuting = tool.status === 'executing';

  return (
    <Badge
      variant="secondary"
      className={cn(
        "gap-1 text-xs",
        isExecuting
          ? "bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300 animate-pulse"
          : "bg-cyan-50 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300"
      )}
    >
      {isExecuting ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Icon className="h-3 w-3" />
      )}
      {label}
      {isExecuting && <span className="ml-1">실행 중...</span>}
    </Badge>
  );
}

interface ChatMessageProps {
  message: ChatMessageType;
  isStreaming?: boolean;
  thinkingContent?: string;
  onRegenerate?: () => void;
  projectId?: string;
  onConfirmArtifact?: (artifactId: string) => Promise<void>;
}

export const ChatMessage = memo(function ChatMessage({
  message,
  isStreaming,
  thinkingContent,
  onRegenerate,
  projectId,
  onConfirmArtifact,
}: ChatMessageProps) {
  const [copied, setCopied] = useState(false);
  const [showThinking, setShowThinking] = useState(true);
  const [savedThinking, setSavedThinking] = useState<string | null>(null);
  const thinkingScrollRef = useRef<HTMLDivElement>(null);
  const isUser = message.role === 'user';

  // 추론 내용을 저장하고 스트리밍 끝나면 접기
  useEffect(() => {
    if (thinkingContent) {
      setSavedThinking(thinkingContent);
      setShowThinking(true);
    } else if (savedThinking && !isStreaming) {
      // 스트리밍이 끝나면 접기 (내용은 유지)
      setShowThinking(false);
    }
  }, [thinkingContent, isStreaming, savedThinking]);

  // 스트리밍 중 자동 스크롤
  useEffect(() => {
    if (isStreaming && thinkingContent && thinkingScrollRef.current) {
      thinkingScrollRef.current.scrollTop = thinkingScrollRef.current.scrollHeight;
    }
  }, [thinkingContent, isStreaming]);

  // 표시할 추론 내용 (스트리밍 중이면 실시간, 아니면 저장된 것)
  const displayThinking = thinkingContent || savedThinking;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={cn(
        'group flex gap-4 px-4 py-6',
        isUser ? 'bg-background' : 'bg-muted/50'
      )}
    >
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback
          className={cn(
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
          )}
        >
          {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 space-y-2 overflow-hidden min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">
            {isUser ? 'You' : 'Assistant'}
          </span>
          {isStreaming && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
              Thinking...
            </span>
          )}
          {/* Tool usage indicators */}
          {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
            <div className="flex items-center gap-1">
              {message.toolCalls.map((tool, idx) => (
                <ToolUsageBadge key={`${tool.type}-${idx}`} tool={tool} />
              ))}
            </div>
          )}
        </div>

        {/* Thinking/Reasoning Content */}
        {!isUser && displayThinking && (
          <div className="mb-3 rounded-lg border border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/50">
            <button
              onClick={() => setShowThinking(!showThinking)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-purple-700 dark:text-purple-300"
            >
              <Brain className="h-4 w-4" />
              <span>추론 과정</span>
              {isStreaming && thinkingContent && (
                <span className="ml-2 h-1.5 w-1.5 animate-pulse rounded-full bg-purple-500" />
              )}
              {showThinking ? (
                <ChevronUp className="ml-auto h-4 w-4" />
              ) : (
                <ChevronDown className="ml-auto h-4 w-4" />
              )}
            </button>
            {showThinking && (
              <div
                ref={thinkingScrollRef}
                className="max-h-48 overflow-y-auto border-t border-purple-200 px-3 py-2 dark:border-purple-800"
              >
                <p className="whitespace-pre-wrap text-sm text-purple-600 dark:text-purple-400">
                  {displayThinking}
                </p>
              </div>
            )}
          </div>
        )}

        {message.content ? (
          <MarkdownRenderer content={message.content} />
        ) : isStreaming ? (
          <div className="flex items-center gap-1">
            <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground delay-100" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground delay-200" />
          </div>
        ) : null}

        {/* Artifacts */}
        {!isUser && message.artifacts && message.artifacts.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-medium text-muted-foreground">생성된 산출물</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {message.artifacts.map((artifact) => (
                <ArtifactCard
                  key={artifact.id}
                  artifact={artifact}
                  projectId={projectId}
                  onConfirm={onConfirmArtifact}
                />
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {!isStreaming && message.content && (
          <div className="flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="mr-1 h-3 w-3" />
              ) : (
                <Copy className="mr-1 h-3 w-3" />
              )}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            {!isUser && onRegenerate && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={onRegenerate}
              >
                <RefreshCw className="mr-1 h-3 w-3" />
                Regenerate
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
